'use client';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
import { isEqual } from 'radash';
import {
  buildMentionOverflowMap,
  type MentionOverflowMap,
} from './mentionOverflow';
import {
  useSetMentionOverflowElements,
  type MentionOverflowElementMap,
} from './MentionOverflowContext';

export function MentionOverflowPlugin() {
  const [editor] = useLexicalComposerContext();
  const setOverflowByElement = useSetMentionOverflowElements();
  const lastByNodeKeyRef = useRef<MentionOverflowMap>({});
  const lastElementByNodeKeyRef = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const syncOverflowMap = () => {
      const byNodeKey = editor
        .getEditorState()
        .read(() => buildMentionOverflowMap());

      const elementByNodeKey = new Map<string, HTMLElement>();
      const byElement: MentionOverflowElementMap = new Map();
      for (const [nodeKey, entry] of Object.entries(byNodeKey)) {
        const element = editor.getElementByKey(nodeKey);
        if (element) {
          elementByNodeKey.set(nodeKey, element);
          byElement.set(element, entry);
        }
      }

      const sameNodeKeys = isEqual(lastByNodeKeyRef.current, byNodeKey);
      const sameElements =
        lastElementByNodeKeyRef.current.size === elementByNodeKey.size &&
        [...elementByNodeKey.entries()].every(
          ([nodeKey, element]) =>
            lastElementByNodeKeyRef.current.get(nodeKey) === element,
        );

      if (sameNodeKeys && sameElements) {
        return;
      }

      lastByNodeKeyRef.current = byNodeKey;
      lastElementByNodeKeyRef.current = elementByNodeKey;
      setOverflowByElement(byElement);
    };

    syncOverflowMap();
    return editor.registerUpdateListener(() => {
      syncOverflowMap();
    });
  }, [editor, setOverflowByElement]);

  return null;
}
